import {
  DEFAULT_SALES_SETTINGS,
  EFFECTIVE_DAYS,
  PROPOSAL_STATUSES,
  PROPOSAL_TYPES,
  daysInStatus,
  daysUntil,
  formatLocalShort,
  kpisFor,
  nextMonth,
  normalizeText,
  staleProposals,
  summarizeLives,
  todayIn,
  upcomingEffective,
  type CommissionTier,
  type ProposalStatus,
  type ProposalType,
} from '@sales-rules';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type ReactNode,
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { errorMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth-context-value';
import {
  brl,
  copyText,
  devicePrefs,
  monthName,
  salesApi,
  statusLabel,
  typeLabel,
  type SalesInterview,
  type SalesPrefill,
  type SalesProposal,
  type SellerRef,
} from '../../lib/sales';
import { useSalesRealtime } from '../../lib/sales-realtime';
import { ProposalCard, type ProposalCardActions } from './ProposalCard';
import { ProposalForm } from './ProposalForm';
import {
  InterviewDialog,
  InterviewLines,
  InterviewReminder,
  InterviewsPanel,
} from './Interviews';
import { ImportDialog, MultiSelect, PendingDialog, TrashDialog } from './SalesDialogs';
import './sales.css';

const FONTS =
  'https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@500;600;700&family=Barlow:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500&display=swap';

/** Fontes do artefato, carregadas só quando a tela de Vendas abre. */
function useSalesFonts() {
  useEffect(() => {
    if (document.querySelector('link[data-sales-fonts]')) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = FONTS;
    link.dataset.salesFonts = 'true';
    document.head.appendChild(link);
  }, []);
}

type StatusFilter = '' | 'ABERTO' | ProposalStatus;

/** Ordem de cadastro (a mais recente embaixo); entre importadas, vigência e nº. */
const byEntry = (a: SalesProposal, b: SalesProposal) =>
  a.createdAt.slice(0, 16).localeCompare(b.createdAt.slice(0, 16)) ||
  a.effectiveDate.localeCompare(b.effectiveDate) ||
  (Number.parseInt(a.requestNumber ?? '', 10) || 0) -
    (Number.parseInt(b.requestNumber ?? '', 10) || 0);

/** "3 propostas · 5 vidas (4 limpas e 1 repique)". */
function LivesLine({ proposals }: { proposals: SalesProposal[] }) {
  const summary = summarizeLives(proposals);
  const parts: ReactNode[] = [];
  if (summary.clean)
    parts.push(
      <span key="l" className="pos">
        {summary.clean} {summary.clean === 1 ? 'limpa' : 'limpas'}
      </span>,
    );
  if (summary.rebound)
    parts.push(
      <span key="r" className="neg">
        {summary.rebound} repique
      </span>,
    );
  if (summary.defaulting)
    parts.push(
      <span key="i" className="neg">
        {summary.defaulting} {summary.defaulting === 1 ? 'inadimplente' : 'inadimplentes'}
      </span>,
    );
  if (summary.unmarked) parts.push(<span key="x">{summary.unmarked} sem situação</span>);
  return (
    <>
      <b>{summary.proposals}</b> {summary.proposals === 1 ? 'proposta' : 'propostas'} ·{' '}
      <b>{summary.lives}</b> {summary.lives === 1 ? 'vida' : 'vidas'}
      {parts.length > 0 && (
        <>
          {' ('}
          {parts.map((part, index) => (
            <span key={index}>
              {index > 0 && (index === parts.length - 1 ? ' e ' : ', ')}
              {part}
            </span>
          ))}
          {')'}
        </>
      )}
    </>
  );
}

/**
 * Tela de Vendas (V1): o painel do vendedor do artefato Minhas Vendas, dentro do CRM.
 * Cada vendedor vê só as próprias propostas; o gestor de vendas vê a equipe e filtra por
 * vendedor. Os dados e o escopo vêm da API; as contas, de `@sales-rules`.
 */
export function SalesPage() {
  useSalesFonts();
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const online = useSalesRealtime();
  const [today, setToday] = useState(() => todayIn());
  useEffect(() => {
    const timer = window.setInterval(() => setToday(todayIn()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const proposalsQuery = useQuery({
    queryKey: ['sales', 'proposals'],
    queryFn: salesApi.list,
    refetchInterval: 60_000,
  });
  const settings = useQuery({ queryKey: ['sales', 'settings'], queryFn: salesApi.settings })
    .data ?? {
    ...DEFAULT_SALES_SETTINGS,
    updatedAt: null,
  };
  const sellersQuery = useQuery({ queryKey: ['sales', 'sellers'], queryFn: salesApi.sellers });
  const me: SellerRef = {
    membershipId: session?.access.membershipId ?? '',
    name: session?.user.name ?? '',
  };
  const meAsSeller = sellersQuery.data?.find((item) => item.membershipId === me.membershipId);
  const isManager = Boolean(meAsSeller?.isManager);
  const sellers: SellerRef[] = sellersQuery.data ?? [];

  // Filtros. Tipo, operadora e vendedor ficam lembrados neste aparelho.
  const [typeFilter, setTypeFilter] = useState(
    () => devicePrefs.get('tipo') as ProposalType | '',
  );
  const [operatorFilter, setOperatorFilter] = useState(() => devicePrefs.get('op'));
  const [sellerFilter, setSellerFilter] = useState(() => devicePrefs.get('vend'));
  const [months, setMonths] = useState<Set<string>>(new Set());
  const [days, setDays] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [query, setQuery] = useState('');
  const [showAllClosed, setShowAllClosed] = useState(false);
  const [commissionOpen, setCommissionOpen] = useState(false);
  const [openAlerts, setOpenAlerts] = useState<Set<string>>(new Set());

  const [formFor, setFormFor] = useState<SalesProposal | null | 'new'>(null);
  const [droppedPdf, setDroppedPdf] = useState<File | null>(null);
  const [interviewFor, setInterviewFor] = useState<SalesProposal | null>(null);
  const [prefill, setPrefill] = useState<SalesPrefill | null>(null);
  const [pendingFor, setPendingFor] = useState<SalesProposal | null>(null);
  const [importOpen, setImportOpen] = useState<{ file: File | null } | null>(null);
  const [trashOpen, setTrashOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [toastText, setToastText] = useState<string | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);

  const toast = useCallback((message: string) => {
    setToastText(message);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToastText(null), 2600);
  }, []);

  // "/vendas?negocio=<id>" (botão no negócio do CRM): abre a proposta já ligada ao lead.
  const [searchParams, setSearchParams] = useSearchParams();
  const fromBusiness = searchParams.get('negocio');
  useEffect(() => {
    if (!fromBusiness) return;
    let active = true;
    salesApi
      .prefillFromBusiness(fromBusiness)
      .then((data) => {
        if (!active) return;
        setPrefill(data);
        setFormFor('new');
      })
      .catch((error) => {
        if (active) toast(errorMessage(error, 'Não achei esse negócio.'));
      })
      .finally(() => {
        if (active) setSearchParams({}, { replace: true });
      });
    return () => {
      active = false;
    };
  }, [fromBusiness, setSearchParams, toast]);

  // A comissão começa escondida e se esconde sozinha em 20 s.
  useEffect(() => {
    if (!commissionOpen) return;
    const timer = window.setTimeout(() => setCommissionOpen(false), 20_000);
    return () => window.clearTimeout(timer);
  }, [commissionOpen]);

  const all = useMemo(() => proposalsQuery.data ?? [], [proposalsQuery.data]);
  const operators = settings.operators;
  const validSeller =
    isManager && sellers.some((item) => item.membershipId === sellerFilter) ? sellerFilter : '';
  const validOperator = operators.includes(operatorFilter) ? operatorFilter : '';
  const validType = PROPOSAL_TYPES.includes(typeFilter as ProposalType) ? typeFilter : '';

  const base = useMemo(
    () =>
      all.filter(
        (proposal) =>
          (!validType || proposal.type === validType) &&
          (!validOperator || proposal.operator === validOperator) &&
          (!validSeller || proposal.seller?.membershipId === validSeller),
      ),
    [all, validType, validOperator, validSeller],
  );
  const withoutStatus = useMemo(
    () =>
      base.filter(
        (proposal) =>
          (!months.size || months.has(proposal.effectiveDate.slice(0, 7))) &&
          (!days.size || days.has(proposal.effectiveDate.slice(8, 10))),
      ),
    [base, months, days],
  );
  const current = useMemo(
    () =>
      withoutStatus.filter(
        (proposal) =>
          !statusFilter ||
          (statusFilter === 'ABERTO'
            ? proposal.status !== 'ENCERRADO'
            : proposal.status === statusFilter),
      ),
    [withoutStatus, statusFilter],
  );
  const filtered = Boolean(
    months.size || days.size || statusFilter || validType || validOperator,
  );

  // Meses: sempre o atual e o seguinte, mais os que têm proposta.
  const monthOptions = useMemo(() => {
    const thisMonth = today.slice(0, 7);
    const set = new Set([
      thisMonth,
      nextMonth(thisMonth),
      ...all.map((p) => p.effectiveDate.slice(0, 7)),
    ]);
    return [...set]
      .sort()
      .reverse()
      .map((month) => ({
        value: month,
        label: monthName(month),
        count: base.filter((proposal) => proposal.effectiveDate.startsWith(month)).length,
      }));
  }, [all, base, today]);

  const commissionMonth = months.size === 1 ? [...months][0] : today.slice(0, 7);
  const summary = useQuery({
    queryKey: ['sales', 'summary', commissionMonth, validSeller],
    queryFn: () => salesApi.summary(commissionMonth, validSeller || undefined),
    enabled: commissionOpen,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['sales'] });

  const changeStatus = useMutation({
    mutationFn: ({
      proposal,
      status,
      text,
    }: {
      proposal: SalesProposal;
      status: ProposalStatus;
      text?: string;
    }) => salesApi.status(proposal.id, status, text),
    onSuccess: (saved) => {
      void invalidate();
      toast(
        `${saved.requestNumber ?? saved.company} agora está em ${statusLabel(saved.status)}`,
      );
    },
    onError: (error) => toast(errorMessage(error, 'Não consegui mudar o status.')),
  });

  const interviewAction = (
    interview: SalesInterview,
    patch: { done?: boolean; reminded?: boolean; snoozeMinutes?: number },
  ) =>
    void salesApi
      .updateInterview(interview.id, patch)
      .then(() => {
        void invalidate();
        if (patch.done) toast('Entrevista marcada como feita');
        if (patch.snoozeMinutes) toast(`Aviso de novo em ${patch.snoozeMinutes} minutos`);
      })
      .catch((error) => toast(errorMessage(error, 'Não consegui salvar agora.')));

  const actions: ProposalCardActions = {
    onCopy: (value, label) =>
      void copyText(value).then((ok) =>
        toast(ok ? `${label} copiado: ${value}` : 'Não consegui copiar aqui'),
      ),
    onStatus: (proposal, status) => {
      if (status === 'PENDENTE') setPendingFor(proposal);
      else changeStatus.mutate({ proposal, status });
    },
    onEdit: (proposal) => setFormFor(proposal),
    onEconomyEnergy: (proposal) =>
      void salesApi
        .economyEnergy(proposal.id, !proposal.economyEnergyAt)
        .then(() => invalidate())
        .catch((error) => toast(errorMessage(error, 'Não consegui marcar agora.'))),
    extra: (proposal) => (
      <InterviewLines
        proposal={proposal}
        onUpdate={(interview, patch) => interviewAction(interview, patch)}
        onDelete={(interview) =>
          void salesApi
            .deleteInterview(interview.id)
            .then(() => {
              void invalidate();
              toast('Lembrete apagado');
            })
            .catch((error) => toast(errorMessage(error, 'Não consegui apagar o lembrete.')))
        }
      />
    ),
    extraActions: (proposal) => (
      <button type="button" className="act" onClick={() => setInterviewFor(proposal)}>
        + Entrevista
      </button>
    ),
    onOpenPdf: (proposal) => {
      // Abre a aba antes do download: o navegador do celular bloqueia janela aberta depois.
      const tab = window.open('', '_blank');
      void salesApi
        .pdfBlob(proposal.id)
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          if (tab) tab.location.href = url;
          else window.location.href = url;
          window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
        })
        .catch((error) => {
          tab?.close();
          toast(errorMessage(error, 'Não consegui abrir o PDF.'));
        });
    },
  };

  const card = (proposal: SalesProposal) => (
    <ProposalCard
      key={proposal.id}
      proposal={proposal}
      today={today}
      staleDays={settings.staleDays}
      showSeller={isManager && !validSeller}
      actions={actions}
    />
  );

  // Busca: com filtro ligado, dentro do filtro; sem filtro, na base toda.
  const searchResults = useMemo(() => {
    const q = normalizeText(query);
    if (q.length < 2) return null;
    const digits = q.replace(/\D/g, '');
    const pool = filtered ? current : all;
    const found = pool.filter(
      (proposal) =>
        (digits.length >= 5 && (proposal.cnpj ?? '').includes(digits)) ||
        normalizeText(proposal.company).includes(q) ||
        normalizeText(proposal.requestNumber) === q ||
        (digits.length >= 3 && (proposal.requestNumber ?? '').includes(digits)) ||
        proposal.beneficiaries.some(
          (item) =>
            normalizeText(item.name).includes(q) ||
            (digits.length >= 4 && (item.cpf ?? '').includes(digits)),
        ),
    );
    found.sort(
      (a, b) =>
        Number(b.requestNumber === digits) - Number(a.requestNumber === digits) ||
        b.effectiveDate.localeCompare(a.effectiveDate),
    );
    return found;
  }, [query, filtered, current, all]);

  const upcoming = useMemo(() => upcomingEffective(current, today), [current, today]);
  const stale = useMemo(
    () => staleProposals(current, settings.staleDays),
    [current, settings.staleDays],
  );
  const kpis = kpisFor(current, today.slice(0, 7));

  const columns: ProposalStatus[] =
    statusFilter && statusFilter !== 'ABERTO'
      ? [statusFilter]
      : statusFilter === 'ABERTO'
        ? PROPOSAL_STATUSES.filter((status) => status !== 'ENCERRADO')
        : [...PROPOSAL_STATUSES];

  const clearFilters = () => {
    setMonths(new Set());
    setDays(new Set());
    setStatusFilter('');
    setTypeFilter('');
    setOperatorFilter('');
    devicePrefs.set('tipo', '');
    devicePrefs.set('op', '');
  };

  const exportSheet = async () => {
    try {
      const blob = await salesApi.exportSheet(validSeller || undefined);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `minhas-vendas-${today}.xlsx`;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (error) {
      toast(errorMessage(error, 'Não consegui baixar agora.'));
    }
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files[0];
    if (!file) return;
    // PDF arrastado em qualquer parte da tela: abre o formulário e já começa a leitura.
    if (/\.pdf$/i.test(file.name)) {
      setDroppedPdf(file);
      setFormFor('new');
      return;
    }
    if (/\.(xlsx|json)$/i.test(file.name)) setImportOpen({ file });
    else toast('Arraste uma planilha .xlsx, o .json do Minhas Vendas ou o PDF da proposta.');
  };

  const companies = useMemo(() => [...new Set(all.map((p) => p.company))].sort(), [all]);
  const plans = useMemo(
    () => [...new Set(all.map((p) => p.plan).filter(Boolean) as string[])].sort(),
    [all],
  );
  const closedLimit = (proposal: SalesProposal) =>
    (daysUntil(proposal.effectiveDate, today) ?? 0) >= -30;

  const loading = proposalsQuery.isLoading;

  return (
    <div
      className={`sv${dragging ? ' drop' : ''}`}
      onDragOver={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(event) => event.target === event.currentTarget && setDragging(false)}
      onDrop={onDrop}
    >
      <div className="wrap">
        <header>
          <div className="hgroup">
            <Link to="/" className="linkbtn" style={{ fontSize: '.85rem' }}>
              ← CRM
            </Link>
            <h1>{isManager && !validSeller ? 'Vendas da equipe' : 'Minhas Vendas'}</h1>
            <div className="src">
              <span className={`dot${online ? ' on' : ''}`} />
              {loading
                ? 'Carregando…'
                : `${all.length} ${all.length === 1 ? 'proposta' : 'propostas'} · salvas na sua conta do CRM${
                    online ? ', sincroniza entre aparelhos' : ''
                  }`}
            </div>
          </div>
          <div className="controls">
            {isManager && (
              <select
                aria-label="Vendedor"
                className={validSeller ? 'on' : ''}
                value={validSeller}
                onChange={(event) => {
                  setSellerFilter(event.target.value);
                  devicePrefs.set('vend', event.target.value);
                }}
              >
                <option value="">Todos os vendedores</option>
                {sellers.map((item) => (
                  <option key={item.membershipId} value={item.membershipId}>
                    {item.name}
                  </option>
                ))}
              </select>
            )}
            <select
              aria-label="Tipo"
              className={validType ? 'on' : ''}
              value={validType}
              onChange={(event) => {
                setTypeFilter(event.target.value as ProposalType | '');
                devicePrefs.set('tipo', event.target.value);
              }}
            >
              <option value="">Todos os tipos</option>
              {PROPOSAL_TYPES.map((type) => (
                <option key={type} value={type}>
                  {typeLabel(type)}
                </option>
              ))}
            </select>
            <select
              aria-label="Operadora"
              className={validOperator ? 'on' : ''}
              value={validOperator}
              onChange={(event) => {
                setOperatorFilter(event.target.value);
                devicePrefs.set('op', event.target.value);
              }}
            >
              <option value="">Todas as operadoras</option>
              {operators.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            <button className="btn ghost" type="button" onClick={() => setFormFor('new')}>
              Ler PDF da proposta
            </button>
            <button className="btn" type="button" onClick={() => setFormFor('new')}>
              + Nova proposta
            </button>
          </div>
        </header>

        <section className="searchbar" aria-label="Buscar">
          <input
            type="search"
            placeholder="Buscar pelo nº da solicitação, nome, empresa ou CPF"
            aria-label="Buscar proposta"
            enterKeyHint="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </section>
        {searchResults && (
          <section className="sres">
            <div className="chhead">
              <h2>
                {searchResults.length
                  ? `${searchResults.length} ${searchResults.length === 1 ? 'proposta encontrada' : 'propostas encontradas'}`
                  : 'Nada encontrado'}
                {filtered ? ' nos filtros selecionados' : ' em toda a base'}
              </h2>
              <button className="linkbtn" type="button" onClick={() => setQuery('')}>
                Limpar busca
              </button>
            </div>
            <div className="results">
              {searchResults.length ? (
                searchResults.slice(0, 30).map(card)
              ) : (
                <div className="empty">Nenhuma proposta com esse número, nome ou CPF.</div>
              )}
            </div>
          </section>
        )}

        <section className="fbar" aria-label="Filtros">
          <div className="fl">
            <span>Mês da vigência</span>
            <MultiSelect
              options={monthOptions}
              selected={months}
              allLabel="Todos os meses"
              format={monthName}
              onChange={setMonths}
            />
          </div>
          <div className="fl">
            <span>Dia</span>
            <MultiSelect
              small
              options={EFFECTIVE_DAYS.map((day) => ({ value: day, label: `Dia ${day}` }))}
              selected={days}
              allLabel="Todos"
              format={(day) => day}
              onChange={setDays}
            />
          </div>
          <label className="fl">
            <span>Status</span>
            <select
              className={statusFilter ? 'on' : ''}
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
            >
              <option value="">Todos</option>
              {PROPOSAL_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {statusLabel(status)}
                </option>
              ))}
              <option value="ABERTO">Em aberto (todos menos Encerrado)</option>
            </select>
          </label>
          {filtered && (
            <button className="linkbtn" type="button" onClick={clearFilters}>
              Limpar filtros
            </button>
          )}
          {filtered && (
            <FilterSummary
              current={current}
              withoutStatus={withoutStatus}
              statusFilter={statusFilter}
              onStatus={setStatusFilter}
            />
          )}
        </section>

        {/* Com um status escolhido, o painel some para as propostas aparecerem logo abaixo. */}
        {!statusFilter && (
          <InterviewsPanel
            proposals={current}
            onDone={(interview) => interviewAction(interview, { done: true })}
          />
        )}

        <section className={`comissao${commissionOpen ? '' : ' closed'}`}>
          <CommissionBlock
            open={commissionOpen}
            onToggle={() => setCommissionOpen(!commissionOpen)}
            month={commissionMonth}
            data={summary.data}
            loading={summary.isLoading}
            team={isManager && !validSeller}
            tiers={settings.commissionTiers}
          />
        </section>
        {commissionOpen && (
          <section className="kpis">
            <Kpi
              label="Em andamento"
              value={kpis.open.proposals}
              sub={`${kpis.open.lives} vidas`}
            />
            <Kpi
              tone="k-ok"
              label="Vidas limpas"
              value={kpis.lives.clean}
              sub="comissionadas"
            />
            <Kpi tone="k-bad" label="Repiques" value={kpis.lives.rebound} sub="vidas" />
            <Kpi tone="k-bad" label="Inadimplentes" value={kpis.lives.defaulting} sub="vidas" />
            <Kpi
              label={`Encerradas em ${monthName(today.slice(0, 7)).split('/')[0]}`}
              value={kpis.closedInMonth.proposals}
              sub={`${kpis.closedInMonth.lives} vidas`}
            />
            {kpis.lives.unmarked > 0 && (
              <Kpi
                tone="k-warn"
                label="Sem situação"
                value={kpis.lives.unmarked}
                sub="vidas para marcar"
              />
            )}
          </section>
        )}

        {!filtered && !loading && (
          <section className="alerts" aria-label="Vigências próximas">
            {upcoming.length ? (
              <>
                <div className="alhead">
                  <h2>Atenção: vigência chegando</h2>
                  <span>
                    Propostas ainda não encerradas com vigência nos próximos 14 dias ou já
                    passada. Toque para ver os dados.
                  </span>
                </div>
                {upcoming.map((proposal) => {
                  const left = daysUntil(proposal.effectiveDate, today) ?? 0;
                  return (
                    <AlertItem
                      key={proposal.id}
                      id={proposal.id}
                      open={openAlerts}
                      setOpen={setOpenAlerts}
                      critical={left < 0}
                      big={
                        left < 0 ? (
                          <>
                            {-left}
                            <small>dias atrás</small>
                          </>
                        ) : left === 0 ? (
                          <>
                            hoje<small>vigência</small>
                          </>
                        ) : (
                          <>
                            {left}
                            <small>{left === 1 ? 'dia' : 'dias'}</small>
                          </>
                        )
                      }
                      title={proposal.company}
                      detail={`${proposal.requestNumber ?? ''} · ainda em ${statusLabel(proposal.status)} · vigência ${proposal.effectiveDate.split('-').reverse().join('/')} · ${proposal.lives} ${proposal.lives === 1 ? 'vida' : 'vidas'}`}
                    >
                      {card(proposal)}
                    </AlertItem>
                  );
                })}
              </>
            ) : (
              <div className="allclear">
                Nenhuma proposta em aberto com vigência nos próximos 14 dias.
              </div>
            )}
            {stale.length > 0 && (
              <>
                <div className="alhead" style={{ marginTop: 8 }}>
                  <h2 style={{ color: 'var(--pen)' }}>Atenção: propostas paradas</h2>
                  <span>
                    Sem mudar de status há mais tempo que o normal (
                    {Object.entries(settings.staleDays)
                      .map(
                        ([status, limit]) =>
                          `${statusLabel(status as ProposalStatus)} ${limit}`,
                      )
                      .join(', ')}{' '}
                    dias). Toque para ver.
                  </span>
                </div>
                {stale.map((proposal) => {
                  const inStatus = daysInStatus(proposal.statusSince) ?? 0;
                  return (
                    <AlertItem
                      key={`p-${proposal.id}`}
                      id={`p-${proposal.id}`}
                      open={openAlerts}
                      setOpen={setOpenAlerts}
                      critical
                      big={
                        <>
                          {inStatus}
                          <small>{inStatus === 1 ? 'dia' : 'dias'}</small>
                        </>
                      }
                      title={proposal.company}
                      detail={`${proposal.requestNumber ?? ''} · parada em ${statusLabel(proposal.status)}${
                        proposal.status === 'PENDENTE' && proposal.pendingText
                          ? ` · ${proposal.pendingText}`
                          : ''
                      } · ${proposal.lives} ${proposal.lives === 1 ? 'vida' : 'vidas'}`}
                    >
                      {card(proposal)}
                    </AlertItem>
                  );
                })}
              </>
            )}
          </section>
        )}

        {!loading && all.length === 0 && (
          <div className="nores">
            Nenhuma proposta ainda. Cadastre a primeira em “+ Nova proposta” ou importe a sua
            planilha.
          </div>
        )}

        {all.length > 0 && filtered && current.length === 0 ? (
          <div className="nores">Nenhuma proposta cadastrada com esses filtros.</div>
        ) : (
          all.length > 0 && (
            <section className={`board${columns.length === 1 ? ' single' : ''}`}>
              {columns.map((status) => {
                const inColumn = current
                  .filter((proposal) => proposal.status === status)
                  .sort(byEntry);
                let shown = inColumn;
                let more: ReactNode = null;
                if (status === 'ENCERRADO' && !filtered) {
                  const recent = inColumn.filter(closedLimit);
                  if (!showAllClosed && inColumn.length > recent.length) {
                    shown = recent;
                    more = (
                      <button
                        className="more"
                        type="button"
                        onClick={() => setShowAllClosed(true)}
                      >
                        Mostrar todas as {inColumn.length} encerradas
                      </button>
                    );
                  } else if (showAllClosed && inColumn.length > 6) {
                    more = (
                      <button
                        className="more"
                        type="button"
                        onClick={() => setShowAllClosed(false)}
                      >
                        Mostrar só as recentes
                      </button>
                    );
                  }
                }
                const sub =
                  status === 'ENCERRADO' && shown.length < inColumn.length
                    ? 'mostrando vigência dos últimos 30 dias'
                    : inColumn.length
                      ? brl(summarizeLives(inColumn).totalCents)
                      : '';
                return (
                  <div className="col" data-s={status} key={status}>
                    <div className="colhead">
                      <h2>{statusLabel(status)} ·</h2>
                      <div className="colres num">
                        {inColumn.length ? (
                          <LivesLine proposals={inColumn} />
                        ) : (
                          'Nenhuma proposta'
                        )}
                      </div>
                      <span className="num colval">{sub}</span>
                    </div>
                    <div className="cards">
                      {shown.length ? (
                        shown.map(card)
                      ) : (
                        <div className="empty">Nenhuma proposta aqui.</div>
                      )}
                    </div>
                    {more}
                  </div>
                );
              })}
            </section>
          )
        )}

        <div className="foot">
          <button className="linkbtn" type="button" onClick={() => void exportSheet()}>
            Baixar cópia em Excel
          </button>
          <button
            className="linkbtn"
            type="button"
            onClick={() => setImportOpen({ file: null })}
          >
            Importar planilha
          </button>
          <button className="linkbtn" type="button" onClick={() => setTrashOpen(true)}>
            Lixeira
          </button>
          <span>Arraste uma planilha .xlsx ou o PDF da proposta para cá.</span>
        </div>
      </div>

      {formFor && (
        <ProposalForm
          proposal={formFor === 'new' ? null : formFor}
          today={today}
          operators={operators}
          isManager={isManager}
          me={me}
          sellers={sellers}
          defaultSeller={validSeller}
          companies={companies}
          plans={plans}
          onClose={() => {
            setDroppedPdf(null);
            setPrefill(null);
            setFormFor(null);
            void invalidate();
          }}
          onSaved={(saved, created) => {
            setDroppedPdf(null);
            setFormFor(null);
            void invalidate();
            toast(
              created
                ? `Proposta ${saved.requestNumber ?? saved.company} cadastrada`
                : 'Proposta salva',
            );
          }}
          onDeleted={(deleted) => {
            setFormFor(null);
            void invalidate();
            toast(`${deleted.requestNumber ?? deleted.company} foi para a lixeira`);
          }}
          initialPdf={droppedPdf}
          prefill={formFor === 'new' ? prefill : null}
        />
      )}
      {pendingFor && (
        <PendingDialog
          proposal={pendingFor}
          onClose={() => setPendingFor(null)}
          onConfirm={async (text) => {
            await changeStatus.mutateAsync({ proposal: pendingFor, status: 'PENDENTE', text });
            setPendingFor(null);
          }}
        />
      )}
      {importOpen && (
        <ImportDialog
          initialFile={importOpen.file}
          isManager={isManager}
          sellers={sellers}
          onClose={() => setImportOpen(null)}
          onDone={(result) => {
            setImportOpen(null);
            void invalidate();
            toast(
              `${result.created + result.updated} propostas importadas (${result.created} novas)`,
            );
          }}
        />
      )}
      {trashOpen && (
        <TrashDialog
          onClose={() => setTrashOpen(false)}
          onChanged={() => void invalidate()}
          toast={toast}
        />
      )}
      {interviewFor && (
        <InterviewDialog
          proposal={interviewFor}
          onClose={() => setInterviewFor(null)}
          onSaved={(_saved, notifyAt) => {
            setInterviewFor(null);
            void invalidate();
            toast(`Lembrete salvo: aviso em ${formatLocalShort(notifyAt)}`);
          }}
        />
      )}
      {/* O aviso fixo é de quem vai ligar para o cliente: só nas propostas da própria pessoa. */}
      <InterviewReminder
        proposals={all.filter((proposal) => proposal.seller?.membershipId === me.membershipId)}
        onAction={interviewAction}
      />
      {toastText && <div className="toast">{toastText}</div>}
    </div>
  );
}

function Kpi({
  label,
  value,
  sub,
  tone = '',
}: {
  label: string;
  value: number;
  sub: string;
  tone?: string;
}) {
  return (
    <div className={`kpi ${tone}`}>
      <span className="lbl">{label}</span>
      <span className="val num">{value}</span>
      <span className="sub num">{sub}</span>
    </div>
  );
}

function AlertItem({
  id,
  open,
  setOpen,
  critical,
  big,
  title,
  detail,
  children,
}: {
  id: string;
  open: Set<string>;
  setOpen: (next: Set<string>) => void;
  critical: boolean;
  big: ReactNode;
  title: string;
  detail: string;
  children: ReactNode;
}) {
  return (
    <details
      className="al"
      open={open.has(id)}
      onToggle={(event) => {
        const next = new Set(open);
        if ((event.target as HTMLDetailsElement).open) next.add(id);
        else next.delete(id);
        if (next.size !== open.size) setOpen(next);
      }}
    >
      <summary className={`alert${critical ? ' crit' : ''}`}>
        <div className="days num">{big}</div>
        <div className="what">
          <b>{title}</b>
          <span className="muted" style={{ fontSize: '.85rem' }}>
            {detail}
          </span>
        </div>
        <span className="chev" aria-hidden="true">
          ▾
        </span>
      </summary>
      <div className="alcard">{children}</div>
    </details>
  );
}

function FilterSummary({
  current,
  withoutStatus,
  statusFilter,
  onStatus,
}: {
  current: SalesProposal[];
  withoutStatus: SalesProposal[];
  statusFilter: StatusFilter;
  onStatus: (status: StatusFilter) => void;
}) {
  const summary = summarizeLives(current);
  return (
    <div className="fsum num">
      <div className="fs-row fs-main">
        <LivesLine proposals={current} />
      </div>
      <div className="fs-row fs-money">
        <span className="pos">
          Limpo <b>{brl(summary.cleanCents)}</b>
        </span>
        {summary.otherCents > 0 && (
          <span className="neg">
            Repique/inadimpl. <b>{brl(summary.otherCents)}</b>
          </span>
        )}
        <span>
          Total <b>{brl(summary.totalCents)}</b>
        </span>
      </div>
      <div className="fs-row fs-chips">
        {PROPOSAL_STATUSES.map((status) => {
          const count = withoutStatus.filter((proposal) => proposal.status === status).length;
          if (!count) return null;
          return (
            <button
              key={status}
              type="button"
              className={`stchip${statusFilter === status ? ' on' : ''}`}
              data-s={status}
              title={
                statusFilter === status
                  ? 'Mostrar todos os status'
                  : `Mostrar só ${statusLabel(status)}`
              }
              onClick={() => onStatus(statusFilter === status ? '' : status)}
            >
              {statusLabel(status)} <b>{count}</b>
            </button>
          );
        })}
        {statusFilter && (
          <button type="button" className="linkbtn" onClick={() => onStatus('')}>
            Todos os status
          </button>
        )}
      </div>
    </div>
  );
}

function CommissionBlock({
  open,
  onToggle,
  month,
  data,
  loading,
  team,
  tiers,
}: {
  open: boolean;
  onToggle: () => void;
  month: string;
  data: Awaited<ReturnType<typeof salesApi.summary>> | undefined;
  loading: boolean;
  team: boolean;
  tiers: CommissionTier[];
}) {
  const name = monthName(month);
  if (!open) {
    return (
      <button type="button" className="cm-toggle" onClick={onToggle}>
        <span className="lbl">
          {team ? 'Comissão da equipe' : 'Comissão'} de {name} e resumo de vidas
        </span>
        <span className="cm-hidden">R$ •••••</span>
        <span className="cm-act">Mostrar</span>
      </button>
    );
  }
  const hide = (
    <button type="button" className="cm-hide" onClick={onToggle}>
      Esconder
    </button>
  );
  if (loading || !data) {
    return (
      <>
        {hide}
        <div className="cm-main">
          <span className="lbl">Comissão de {name}</span>
          <span className="cm-hidden">Calculando…</span>
        </div>
      </>
    );
  }
  if (data.bySeller && data.team) {
    return (
      <>
        {hide}
        <div className="cm-main">
          <span className="lbl">Comissão da equipe em {name}</span>
          <span className="cm-val num">{brl(data.team.totalCents)}</span>
          <span className="sub num">
            {data.team.cleanLives} vidas limpas encerradas · cada vendedor na sua faixa
          </span>
        </div>
        <div className="cm-side num">
          {/* Ranking: do maior para o menor, com o que cada um tem em andamento. */}
          {data.bySeller.map((item, index) => (
            <span key={item.seller?.membershipId ?? `x${index}`}>
              {index + 1}. {item.seller?.name ?? item.sellerName ?? 'Sem vendedor'}:{' '}
              <b>{brl(item.commission.totalCents)}</b>{' '}
              <span className="muted">
                ({item.commission.cleanLives} × {brl(item.commission.perLifeCents)} · em
                andamento: {item.open.proposals}{' '}
                {item.open.proposals === 1 ? 'proposta' : 'propostas'}, {item.open.lives}{' '}
                {item.open.lives === 1 ? 'vida' : 'vidas'})
              </span>
            </span>
          ))}
          {data.bySeller.length === 0 && (
            <span className="muted">Nenhuma venda encerrada no mês.</span>
          )}
        </div>
      </>
    );
  }
  const commission = data.commission;
  if (!commission) return hide;
  return (
    <>
      {hide}
      <div className="cm-main">
        <span className="lbl">Comissão de {name}</span>
        <span className="cm-val num">{brl(commission.totalCents)}</span>
        <span className="sub num">
          {commission.cleanLives}{' '}
          {commission.cleanLives === 1 ? 'vida limpa encerrada' : 'vidas limpas encerradas'} ×{' '}
          {brl(commission.perLifeCents)}
        </span>
      </div>
      <div className="cm-side num">
        {commission.next ? (
          <span>
            Faltam <b>{commission.next.missing}</b>{' '}
            {commission.next.missing === 1 ? 'vida' : 'vidas'} para todas valerem{' '}
            <b>{brl(commission.next.perLifeCents)}</b>
          </span>
        ) : (
          <span>
            <b>Faixa máxima</b> atingida ({brl(commission.perLifeCents)} por vida)
          </span>
        )}
        {commission.openCleanLives > 0 && (
          <span className="muted">
            {commission.openCleanLives === 1
              ? 'Se a vida limpa em andamento fechar: '
              : `Se as ${commission.openCleanLives} vidas limpas em andamento fecharem: `}
            <b>{brl(commission.projectedCents)}</b>
          </span>
        )}
        <span className="muted">{tiersText(tiers)}</span>
      </div>
    </>
  );
}

/** "Faixas: até 10 vidas R$ 100 · 11 a 20 R$ 150 · 21 ou mais R$ 200". */
function tiersText(tiers: CommissionTier[]): string {
  let from = 1;
  const parts = tiers.map((tier, index) => {
    const value = brl(tier.perLifeCents).replace(',00', '');
    const text =
      tier.upTo === null
        ? `${from} ou mais ${value}`
        : index === 0
          ? `até ${tier.upTo} vidas ${value}`
          : `${from} a ${tier.upTo} ${value}`;
    if (tier.upTo !== null) from = tier.upTo + 1;
    return text;
  });
  return `Faixas: ${parts.join(' · ')}`;
}
